import { batch } from 'react-redux';
import { getConferenceState } from '../base/conference/functions';
import { JitsiConferenceEvents } from '../base/lib-jitsi-meet';
import { MEDIA_TYPE as TRACK_MEDIA_TYPE } from '../base/media/constants';
import { isAudioMuted } from '../base/media/functions';
import { PARTICIPANT_UPDATED } from '../base/participants/actionTypes';
import { getLocalParticipant, getRemoteParticipants, hasRaisedHand, isLocalParticipantModerator, isParticipantModerator } from '../base/participants/functions';
import MiddlewareRegistry from '../base/redux/MiddlewareRegistry';
import StateListenerRegistry from '../base/redux/StateListenerRegistry';
import { muteLocal } from '../video-menu/actions.any';
import { DISABLE_MODERATION, ENABLE_MODERATION, LOCAL_PARTICIPANT_APPROVED, LOCAL_PARTICIPANT_MODERATION_NOTIFICATION, LOCAL_PARTICIPANT_REJECTED, PARTICIPANT_APPROVED, PARTICIPANT_REJECTED, REQUEST_DISABLE_AUDIO_MODERATION, REQUEST_DISABLE_DESKTOP_MODERATION, REQUEST_DISABLE_VIDEO_MODERATION, REQUEST_ENABLE_AUDIO_MODERATION, REQUEST_ENABLE_DESKTOP_MODERATION, REQUEST_ENABLE_VIDEO_MODERATION } from './actionTypes';
import { disableModeration, dismissPendingAudioParticipant, dismissPendingParticipant, enableModeration, localParticipantApproved, localParticipantRejected, participantApproved, participantPendingAudio, participantRejected } from './actions';
import { MEDIA_TYPE } from './constants';
import { isEnabledFromState, isParticipantApproved, isParticipantPending } from './functions';
MiddlewareRegistry.register(({ dispatch, getState }) => next => action => {
    const { type } = action;
    const { conference } = getConferenceState(getState());
    switch (type) {
        case LOCAL_PARTICIPANT_MODERATION_NOTIFICATION: {
            // Belltroop: Suppressed popup notification for moderation in effect.
            // The native mic button visual state communicates this instead.
            break;
        }
        case REQUEST_DISABLE_AUDIO_MODERATION: {
            conference?.disableAVModeration(MEDIA_TYPE.AUDIO);
            break;
        }
        case REQUEST_DISABLE_DESKTOP_MODERATION: {
            conference?.disableAVModeration(MEDIA_TYPE.DESKTOP);
            break;
        }
        case REQUEST_DISABLE_VIDEO_MODERATION: {
            conference?.disableAVModeration(MEDIA_TYPE.VIDEO);
            break;
        }
        case REQUEST_ENABLE_AUDIO_MODERATION: {
            conference?.enableAVModeration(MEDIA_TYPE.AUDIO);
            break;
        }
        case REQUEST_ENABLE_DESKTOP_MODERATION: {
            conference?.enableAVModeration(MEDIA_TYPE.DESKTOP);
            break;
        }
        case REQUEST_ENABLE_VIDEO_MODERATION: {
            conference?.enableAVModeration(MEDIA_TYPE.VIDEO);
            break;
        }
        case PARTICIPANT_UPDATED: {
            const state = getState();
            const audioModerationEnabled = isEnabledFromState(MEDIA_TYPE.AUDIO, state);
            const participant = action.participant;
            if (participant && audioModerationEnabled) {
                if (isLocalParticipantModerator(state)) {
                    // this is handled only by moderators
                    if (hasRaisedHand(participant)) {
                        // if participant raises hand show notification
                        !isParticipantApproved(participant.id, MEDIA_TYPE.AUDIO)(state)
                            && dispatch(participantPendingAudio(participant));
                    }
                    else {
                        // if participant lowers hand hide notification
                        isParticipantPending(participant, MEDIA_TYPE.AUDIO)(state)
                            && dispatch(dismissPendingAudioParticipant(participant));
                    }
                }
                else if (participant.id === getLocalParticipant(state)?.id
                    && /* the new role */ isParticipantModerator(participant)) {
                    // this is the granted moderator case
                    getRemoteParticipants(state).forEach(p => {
                        hasRaisedHand(p) && !isParticipantApproved(p.id, MEDIA_TYPE.AUDIO)(state)
                            && dispatch(participantPendingAudio(p));
                    });
                }
            }
            break;
        }
        case ENABLE_MODERATION: {
            if (typeof APP !== 'undefined') {
                APP.API.notifyModerationChanged(action.mediaType, true);
            }
            break;
        }
        case DISABLE_MODERATION: {
            if (typeof APP !== 'undefined') {
                APP.API.notifyModerationChanged(action.mediaType, false);
            }
            break;
        }
        case LOCAL_PARTICIPANT_APPROVED: {
            if (typeof APP !== 'undefined') {
                const local = getLocalParticipant(getState());
                APP.API.notifyParticipantApproved(local?.id, action.mediaType);
            }
            break;
        }
        case PARTICIPANT_APPROVED: {
            if (typeof APP !== 'undefined') {
                APP.API.notifyParticipantApproved(action.id, action.mediaType);
            }
            break;
        }
        case LOCAL_PARTICIPANT_REJECTED: {
            if (typeof APP !== 'undefined') {
                const local = getLocalParticipant(getState());
                APP.API.notifyParticipantRejected(local?.id, action.mediaType);
            }
            break;
        }
        case PARTICIPANT_REJECTED: {
            if (typeof APP !== 'undefined') {
                APP.API.notifyParticipantRejected(action.id, action.mediaType);
            }
            break;
        }
    }
    return next(action);
});
/**
 * Registers a change handler for state['features/base/conference'].conference to
 * set the event listeners needed for the A/V moderation feature to operate.
 */
StateListenerRegistry.register(state => state['features/base/conference'].conference, (conference, { dispatch, getState }, previousConference) => {
    if (conference && !previousConference) {
        // Belltroop: track when each media type was last approved by the
        // admin. We use this to debounce out-of-order focus-mute events
        // that would otherwise wipe a fresh approval — see the
        // *_MUTED_BY_FOCUS listeners below.
        //
        // Race scenario: admin clicks Mute, then Ask-to-Unmute. The mute
        // IQ travels Admin → Jicofo → Participant. The approve message
        // travels Admin → Prosody av_moderation component → Participant.
        // These are independent paths with NO ordering guarantee. If
        // approve arrives FIRST and mute arrives SECOND (within a few
        // hundred ms), the focus-mute listener resets audioUnmuteApproved
        // back to false even though admin's INTENT (last action) was
        // approve. Result: participant's mic button stays blocked.
        const lastApprovedAt = {};
        const APPROVE_DEBOUNCE_MS = 1500;
        // local participant is allowed to unmute
        // Belltroop: Only dispatch approval to Redux state — no popup or sound.
        // The native mic button reads audioUnmuteApproved to enable itself.
        conference.on(JitsiConferenceEvents.AV_MODERATION_APPROVED, ({ mediaType }) => {
            lastApprovedAt[mediaType] = Date.now();
            dispatch(localParticipantApproved(mediaType));
        });
        conference.on(JitsiConferenceEvents.AV_MODERATION_REJECTED, ({ mediaType }) => {
            // Belltroop: co-hosts ignore moderation rejection — they have
            // independent audio/video/desktop control. Without this guard,
            // an admin enabling moderation while a cohost is unmuted would
            // force-mute the cohost.
            if (getLocalParticipant(getState())?.isCohost) {
                return;
            }
            dispatch(localParticipantRejected(mediaType));
            // Belltroop: Force mute the participant when admin revokes permission
            if (mediaType === MEDIA_TYPE.AUDIO && !isAudioMuted(getState())) {
                dispatch(muteLocal(true, TRACK_MEDIA_TYPE.AUDIO));
            }
        });
        // Belltroop: When the local participant is muted by a moderator
        // (the standard mute IQ — separate from AV moderation), reset
        // our own audioUnmuteApproved/videoUnmuteApproved/desktopUnmuteApproved
        // so we need re-approval before unmuting.
        //
        // The check `Date.now() - lastApprovedAt[mt] < APPROVE_DEBOUNCE_MS`
        // guards against the race described above: if an approval just
        // arrived within the last 1.5s, treat the mute IQ as a stale
        // pre-approval message and IGNORE the reset. Admin's intended
        // last state was approve, so participant keeps approval.
        //
        // We previously listened to TRACK_MUTE_CHANGED with actorParticipant
        // requirement, which failed for "Mute All" because actor JID
        // resolution races. Now we listen to chatRoom xmpp.* events
        // which fire reliably for every focus-initiated mute.
        const room = conference.room;
        const safeReject = (mt) => {
            // Belltroop: co-hosts keep their approval flag through any
            // focus-initiated mute. Their mic button must stay enabled
            // so they can immediately unmute themselves.
            if (getLocalParticipant(getState())?.isCohost) {
                return;
            }
            const since = Date.now() - (lastApprovedAt[mt] || 0);
            if (since < APPROVE_DEBOUNCE_MS) {
                // Recent approval — this mute IQ likely arrived out of
                // order. Skip the reset to preserve admin's intent.
                return;
            }
            dispatch(localParticipantRejected(mt));
        };
        if (room && typeof room.on === 'function') {
            room.on('xmpp.audio_muted_by_focus', () => safeReject(MEDIA_TYPE.AUDIO));
            room.on('xmpp.video_muted_by_focus', () => safeReject(MEDIA_TYPE.VIDEO));
            room.on('xmpp.desktop_muted_by_focus', () => safeReject(MEDIA_TYPE.DESKTOP));
        }
        conference.on(JitsiConferenceEvents.AV_MODERATION_CHANGED, ({ enabled, mediaType, actor }) => {
            enabled ? dispatch(enableModeration(mediaType, actor)) : dispatch(disableModeration(mediaType, actor));
        });
        // this is received by moderators
        conference.on(JitsiConferenceEvents.AV_MODERATION_PARTICIPANT_APPROVED, ({ participant, mediaType }) => {
            const { _id: id } = participant;
            batch(() => {
                // store in the whitelist
                dispatch(participantApproved(id, mediaType));
                // remove from pending list
                dispatch(dismissPendingParticipant(id, mediaType));
            });
        });
        // this is received by moderators
        conference.on(JitsiConferenceEvents.AV_MODERATION_PARTICIPANT_REJECTED, ({ participant, mediaType }) => {
            const { _id: id } = participant;
            dispatch(participantRejected(id, mediaType));
        });
    }
});
